import _ from 'lodash';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  elementToCalculateStripBeatThumbnailInfo,
  setStripBeatThumbnailInfo,
} from '@/shared/config';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { ID_DIV_STRIP_BEATS } from '@/widgets/beats/ui/helper';
import { hasTemplateBeat } from '@/widgets/beats/ui/reviewBeatsHelper';
import { SIZE_BEAT_THUMBNAIL, useReportStore } from '@/entities/report';
import type { BeatThumbnailConfigProps } from './beatThumbnailConfig';

/**
 * Drives the beat strip thumbnail toolbar — size, gain, high pass and channel selection — and keeps the thumbnail grid geometry in sync. Mirrors the report store values into local state, debounces channel, gain and high-pass writes back to the store by 500ms, recomputes stripPerRow and stripRow whenever the size preset, the stored config or the active tab changes, and exposes the dropdown handlers alongside the ECG data map.
 *
 * @param activeButton - Currently selected beat event tab, which decides whether the template beat right column is subtracted from the available width.
 * @returns An object with the ECG data map, the current highPass, size, gain and channels, the channel dropdown visibility state and its setter, and the size, gain, high-pass and channel change handlers.
 */
const useBeatThumbnailConfig = ({
  activeButton,
}: Pick<BeatThumbnailConfigProps, 'activeButton'>) => {
  const sizeThumbnailConfig = useReportStore((s) => s.sizeBeatThumbnailConfig);
  const setSizeThumbnailConfig = useReportStore((s) => s.actions.setSizeBeatThumbnailConfig);
  /**
   * Reads the current beat thumbnail size configuration straight from the report store. Pulls sizeBeatThumbnailConfig off the store snapshot instead of subscribing, so handlers and effects always see the latest value.
   *
   * @returns The active beat thumbnail size configuration.
   */
  const getSizeThumbnailConfig = () => useReportStore.getState().sizeBeatThumbnailConfig;
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const setChannelsThumbnail = useReportStore((s) => s.actions.setChannelsThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const setGainThumbnail = useReportStore((s) => s.actions.setGainThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const setHighPassThumbnail = useReportStore((s) => s.actions.setHighPassThumbnail);
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);

  const [highPass, setHighPass] = useState(highPassThumbnail);
  const [size, setSize] = useState(sizeThumbnailConfig.name);
  const [gain, setGain] = useState(gainThumbnail);
  const [channels, setChannels] = useState(channelsThumbnail);

  const [state, setState] = useMergeState({
    visibleDropdownChannel: false,
  });

  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const wrapperHeight = useRef<number>();

  /**
   * Applies the thumbnail size preset picked from the beat thumbnail size dropdown. Stores the selected menu item value as local size, which an effect then resolves to a width and height and recomputes the thumbnail grid from.
   *
   * @param item - Selected dropdown menu item carrying the size name in its props value.
   * @returns Nothing.
   */
  const onClickItemSize = ({ item }: { item: any }) => {
    setSize(item.props.value);
  };

  /**
   * Applies the gain picked from the beat thumbnail gain dropdown. Stores the selected menu item value as local gain, which a debounced effect later pushes into the report store.
   *
   * @param item - Selected dropdown menu item carrying the gain in its props value.
   * @returns Nothing.
   */
  const onClickItemGain = ({ item }: { item: any }) => {
    setGain(item.props.value);
  };

  /**
   * Applies the high-pass filter picked from the beat thumbnail dropdown. Stores the selected menu item value as local high pass, which a debounced effect later pushes into the report store.
   *
   * @param item - Selected dropdown menu item carrying the high-pass value in its props value.
   * @returns Nothing.
   */
  const onClickItemHighPass = ({ item }: { item: any }) => {
    setHighPass(item.props.value);
  };

  /**
   * Computes how many beat strip thumbnails fit per row and per column inside the beats strip container. Caches the container offsetHeight while it is non-zero (a hidden element reports 0), subtracts the left column, side padding and — for template beat tabs — the right column from the window width, floors that space (minimum 1462px) by thumbnail width plus gap, and stores the resulting stripPerRow and stripRow.
   *
   * @param width - Thumbnail width in pixels used as the horizontal step per strip.
   * @param height - Thumbnail height in pixels used as the vertical step per strip.
   * @returns Nothing.
   */
  const calculateStripThumbnailInfo = ({ width, height }: { width: number; height: number }) => {
    const stripWrapperEl = document.getElementById(ID_DIV_STRIP_BEATS);
    if (stripWrapperEl) {
      // When element is hidden, offsetHeight === 0
      if (stripWrapperEl.offsetHeight) {
        wrapperHeight.current = stripWrapperEl.offsetHeight;
      }
      const { leftColumnWidth, paddingBothSide, stripBeatGap, paddingTopBottom, rightColumnWidth } =
        elementToCalculateStripBeatThumbnailInfo;
      const widthAvailable =
        window.innerWidth -
        leftColumnWidth -
        paddingBothSide -
        (hasTemplateBeat(activeButton) ? rightColumnWidth : 0);
      const spaceWidth = _.max([widthAvailable, 1462]); // 1462 is min width of strip box
      const stripPerRow = Math.floor(spaceWidth / (width + stripBeatGap));
      const stripRow = Math.floor(
        (wrapperHeight.current - paddingTopBottom) / (height + stripBeatGap),
      );
      setStripBeatThumbnailInfo({ stripPerRow, stripRow });
    }
  };

  /**
   * Updates the set of ECG channels drawn on beat thumbnails when a channel checkbox toggles. Parses the value to an integer, lets channel 4 (the AI channel) replace the whole selection, otherwise drops the AI channel, adds or removes the toggled channel, sorts the result, falls back to the AI channel when nothing is left, and skips the state write when the array is unchanged.
   *
   * @param name - Unused checkbox field name supplied by the change event.
   * @param value - Channel number as a string from the toggled checkbox.
   * @param checked - Whether the channel was checked on or off.
   * @returns Nothing.
   */
  const onChangeChannels = (name: any, value: any, checked: boolean) => {
    const intValue = parseInt(value, 10);
    const defaultChannels = [4];
    if (intValue === 4) {
      setChannels((prev) => {
        if (_.isEqual(defaultChannels, prev)) {
          return prev;
        }
        return defaultChannels;
      });
      return;
    }
    const newChannels = [...channels];
    const indexAIChannel = newChannels.indexOf(4);
    if (indexAIChannel > -1) {
      newChannels.splice(indexAIChannel, 1);
    }
    const index = newChannels.indexOf(intValue);
    if (checked) {
      if (index === -1) {
        newChannels.push(intValue);
      }
    } else if (index > -1) {
      newChannels.splice(index, 1);
    }
    newChannels.sort();
    setChannels((prev) => {
      if (_.isEqual(newChannels, prev)) {
        return prev;
      }
      if (_.isEmpty(newChannels)) {
        return defaultChannels;
      }
      return newChannels;
    });
  };

  useEffect(() => {
    timeout.current = setTimeout(() => {
      if (!_.isEqual(channels, channelsThumbnail)) {
        setChannelsThumbnail(channels);
      }
      if (gain !== gainThumbnail) {
        setGainThumbnail(gain);
      }
      if (highPass !== highPassThumbnail) {
        setHighPassThumbnail(highPass);
      }
    }, 500);
    return () => {
      clearTimeout(timeout.current);
    };
  }, [channels, gain, highPass]);

  useEffect(() => {
    if (!_.isEqual(channels, channelsThumbnail)) {
      setChannels(channelsThumbnail);
    }
    if (gain !== gainThumbnail) {
      setGain(gainThumbnail);
    }
    if (highPass !== highPassThumbnail) {
      setHighPass(highPassThumbnail);
    }
  }, [channelsThumbnail, gainThumbnail, highPassThumbnail]);

  useEffect(() => {
    if (size !== sizeThumbnailConfig.name) {
      const newSizeThumbnail = SIZE_BEAT_THUMBNAIL[size];
      calculateStripThumbnailInfo({
        width: newSizeThumbnail.width,
        height: newSizeThumbnail.height,
      });
      setSizeThumbnailConfig(newSizeThumbnail);
    }
  }, [size]);

  useEffect(() => {
    if (size !== sizeThumbnailConfig.name) {
      calculateStripThumbnailInfo({
        width: sizeThumbnailConfig.width,
        height: sizeThumbnailConfig.height,
      });
      setSize(sizeThumbnailConfig.name);
    }
  }, [sizeThumbnailConfig]);

  useLayoutEffect(() => {
    const { width, height } = sizeThumbnailConfig;
    calculateStripThumbnailInfo({ width, height });
  }, []);

  useUpdateEffect(() => {
    const sizeThumbnailConfig = getSizeThumbnailConfig();
    const { width, height } = sizeThumbnailConfig || {};
    calculateStripThumbnailInfo({ width, height });
  }, [activeButton]);

  return {
    ecgDataMap,
    highPass,
    size,
    gain,
    channels,
    state,
    setState,
    onClickItemSize,
    onClickItemGain,
    onClickItemHighPass,
    onChangeChannels,
  };
};

export default useBeatThumbnailConfig;
